import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
 Mail, Lock, Eye, EyeOff, ArrowRight, User, CheckCircle2,
 MessageSquare, Camera, Phone, Gamepad2, Gift, CheckSquare, Trophy, Bike, Download, Calendar, Heart, Instagram
} from "lucide-react";
import { formatToBRLDate, parseBRLDateToDB } from "@/utils/dateFormatter";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { TypeAnimation } from "react-type-animation";
import heroBg from "@/assets/hero-bg.jpg";
import PortfolioImersivo from "@/components/PortfolioImersivo";

// Version: 1.0.1 - PWA Update Trigger

const Login = () => {
 const navigate = useNavigate();
 const [email, setEmail] = useState("");
 const [password, setPassword] = useState("");
 const [showPassword, setShowPassword] = useState(false);
 const [loading, setLoading] = useState(false);
 const [rememberMe, setRememberMe] = useState(true);
 const [isSignUp, setIsSignUp] = useState(false);
 const [isForgotPassword, setIsForgotPassword] = useState(false);
 const [resetEmailSent, setResetEmailSent] = useState(false);

 // Instagram Quick Login fields
 const [isInstagramLogin, setIsInstagramLogin] = useState(false);
 const [instagramUser, setInstagramUser] = useState("");
 const [instagramPhone, setInstagramPhone] = useState("");
 const [acceptTerms, setAcceptTerms] = useState(false);

 // Registration fields
 const [name, setName] = useState("");
 const [phone, setPhone] = useState("");
 const [birthDate, setBirthDate] = useState("");
 const [gender, setGender] = useState("");
 const [avatarFile, setAvatarFile] = useState<File | null>(null);
 const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
 const [isPlayer, setIsPlayer] = useState(false);
 const [team, setTeam] = useState("");
 const [number, setNumber] = useState("");
 
 const [isVaqueiro, setIsVaqueiro] = useState(false);
 const [horseName, setHorseName] = useState("");
 
 const [isMotocross, setIsMotocross] = useState(false);
 const [isNoiva, setIsNoiva] = useState(false);

 const [lastUser, setLastUser] = useState<{ name: string; avatar: string | null; email?: string } | null>(null);
 const [appLogoUrl, setAppLogoUrl] = useState("");
 const [appLogoSize, setAppLogoSize] = useState("160");
 const [supportPhone, setSupportPhone] = useState("5511999999999");
 const [supportMsg, setSupportMsg] = useState("Olá! Preciso de ajuda...");
 const [bgSlides, setBgSlides] = useState<string[]>([]);
 const [currentBg, setCurrentBg] = useState(0);
 const [isInstalled, setIsInstalled] = useState(false);

 useEffect(() => {
 const checkInstalled = () => {
 const standalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
 setIsInstalled(standalone);
 };
 
 checkInstalled();
 const mediaQuery = window.matchMedia('(display-mode: standalone)');
 if (mediaQuery.addEventListener) {
 mediaQuery.addEventListener('change', checkInstalled);
 return () => mediaQuery.removeEventListener('change', checkInstalled);
 }
 }, []);

 useEffect(() => {
 if (bgSlides.length <= 1) return;
 const interval = setInterval(() => {
 setCurrentBg(prev => (prev + 1) % bgSlides.length);
 }, 6000);
 return () => clearInterval(interval);
 }, [bgSlides]);

 useEffect(() => {
 fetchSiteSettings();
 }, []);

  useEffect(() => {
    // Check for OAuth errors in URL
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const searchParams = new URLSearchParams(window.location.search);
    
    const error = hashParams.get("error") || searchParams.get("error");
    const errorDescription = hashParams.get("error_description") || searchParams.get("error_description");

    if (error) {
      console.error("OAuth Error:", error, errorDescription);
      toast.error(`Erro no login: ${errorDescription || error}`);
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("last_logged_user");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setLastUser(parsed);
        setEmail(parsed.email || "");
      } catch (e) {
        console.error("Error parsing last_logged_user", e);
        localStorage.removeItem("last_logged_user");
      }
    }
  }, []);

 const fetchSiteSettings = async () => {
 try {
 const cachedLogo = localStorage.getItem("app_logo_url");
 const cachedSize = localStorage.getItem("app_logo_size");
 const cachedPhone = localStorage.getItem("support_whatsapp");
 const cachedMsg = localStorage.getItem("support_message");
 if (cachedLogo) setAppLogoUrl(cachedLogo);
 if (cachedSize) setAppLogoSize(cachedSize);
 if (cachedPhone) setSupportPhone(cachedPhone);
 if (cachedMsg) setSupportMsg(cachedMsg);

 const { data } = await (supabase.from("site_settings" as any).select("*") as any);
 if (data && Array.isArray(data)) {
 const settings: Record<string, string> = {};
 data.forEach((s: any) => { settings[s.key] = s.value; });
 if (settings.app_logo_url) { setAppLogoUrl(settings.app_logo_url); localStorage.setItem("app_logo_url", settings.app_logo_url); }
 if (settings.app_logo_size) { setAppLogoSize(settings.app_logo_size); localStorage.setItem("app_logo_size", settings.app_logo_size); }
 if (settings.support_whatsapp) { setSupportPhone(settings.support_whatsapp); localStorage.setItem("support_whatsapp", settings.support_whatsapp); }
 if (settings.support_message) { setSupportMsg(settings.support_message); localStorage.setItem("support_message", settings.support_message); }
 if (settings.login_bg_slides) { setBgSlides(JSON.parse(settings.login_bg_slides)); }
 }
 } catch (err) { console.error("Error fetching branding:", err); }
 };

 const handleSupport = () => {
 window.open(`https://wa.me/${supportPhone}?text=${encodeURIComponent(supportMsg)}`, "_blank");
 };

 const handleResetPassword = async (e: React.FormEvent) => {
 e.preventDefault();
 if (!email) {
 toast.error("Por favor, insira seu e-mail para recuperar a senha.");
 return;
 }
 setLoading(true);
 try {
 const resetUrl = `${window.location.origin}/reset-password`;
 console.log("[Login] Enviando reset para:", email.trim(), "Redirect:", resetUrl);
 
 const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
 redirectTo: resetUrl,
 });
 
 if (error) throw error;
 setResetEmailSent(true);
 toast.success("Link de recuperação enviado! Verifique seu e-mail.");
 } catch (error: any) {
 console.error("[Login] Erro ao recuperar senha:", error);
 toast.error(error.message || "Erro ao enviar e-mail de recuperação.");
 } finally { setLoading(false); }
 };

 const handleLogin = async (e: React.FormEvent) => {
 e.preventDefault();
 setLoading(true);
 const cleanEmail = email.trim().toLowerCase();
 const cleanPassword = password; // Do not trim password as spaces might be intentional
 
 console.log("[Login] Tentando entrar com:", cleanEmail);

 try {
 const { data, error } = await supabase.auth.signInWithPassword({ 
 email: cleanEmail, 
 password: cleanPassword
 });
 
 if (error) {
 console.error("[Login] Erro do Supabase Auth:", error.message, error);
 
 if (error.message.includes("Email not confirmed")) {
 toast.error("Seu e-mail ainda não foi confirmado. Verifique sua caixa de entrada e clique no link de ativação.");
 throw error;
 }
 
 if (error.message.includes("Invalid login credentials")) {
 toast.error("E-mail ou senha incorretos. Verifique os dados ou tente 'Esqueci a senha'.");
 throw error;
 }
 
 throw error;
 }
 
 if (!data?.user) throw new Error("Falha ao obter dados do usuário após login.");
 
 if (rememberMe) {
 const { data: profile } = await supabase.from("profiles").select("full_name, avatar_url").eq("user_id", data.user.id).maybeSingle();
 if (profile) {
 localStorage.setItem("last_logged_user", JSON.stringify({ 
 name: profile.full_name || "Usuário", 
 avatar: profile.avatar_url, 
 email: cleanEmail 
 }));
 }
 }
 
 toast.success("Bem-vindo de volta!");
 navigate("/home", { replace: true });
 } catch (error: any) {
 console.error("[Login] Exceção detalhada:", JSON.stringify(error, null, 2));
 toast.error(error.message || "Erro inesperado ao entrar. Tente novamente.");
 } finally { 
 setLoading(false); 
 }
 };

 const handleInstagramQuickLogin = async (e: React.FormEvent) => {
 e.preventDefault();
 if (!instagramUser) {
 toast.error("Por favor, insira seu usuário do Instagram.");
 return;
 }
 if (!acceptTerms) {
 toast.error("Você precisa autorizar os Termos de Entrada Rápida para continuar.");
 return;
 }

 setLoading(true);
 const cleanIg = instagramUser.trim().replace("@", "").toLowerCase();

 try {
 console.log("[Login] Solicitando entrada rápida para Instagram:", `@${cleanIg}`);
 
 const { data, error } = await supabase.functions.invoke("manage-subscription", {
 body: {
 action: "quick_instagram_login",
 instagram: `@${cleanIg}`,
 phone: instagramPhone.trim()
 }
 });

 if (error || !data?.email) {
 throw new Error(error?.message || "Erro ao processar Entrada Rápida.");
 }

 // Agora fazemos login direto com as credenciais virtuais retornadas de forma segura e transparente!
 const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
 email: data.email,
 password: data.password
 });

 if (authError) throw authError;

 // Salvar último usuário logado
 if (rememberMe) {
 localStorage.setItem("last_logged_user", JSON.stringify({
 name: `@${cleanIg}`,
 avatar: null,
 email: data.email
 }));
 }

 toast.success("Entrada Rápida via Instagram autorizada com sucesso! 📸");
 navigate("/home", { replace: true });
 } catch (err: any) {
 console.error("[Login] Erro na Entrada Rápida via Instagram:", err);
 toast.error(err.message || "Erro inesperado ao realizar Entrada Rápida.");
 } finally {
 setLoading(false);
 }
 };

 const handleSignUp = async (e: React.FormEvent) => {
 e.preventDefault();
 const cleanName = name.trim();
 const cleanEmail = email.trim().toLowerCase();
 const cleanPhone = phone.trim();
 const cleanPassword = password;

 if (!cleanName || cleanName.split(" ").length < 2) { 
 toast.error("Por favor, insira seu nome completo."); 
 return; 
 }
 if (!cleanPhone || cleanPhone.length < 10) { 
 toast.error("Insira um número de WhatsApp válido."); 
 return; 
 }
 if (!birthDate) { toast.error("Sua data de nascimento é obrigatória."); return; }
 if (!gender) { toast.error("O campo gênero é obrigatório."); return; }
 if (!cleanEmail || !cleanPassword) { toast.error("E-mail e senha são obrigatórios."); return; }
 
 setLoading(true);
 console.log("[SignUp] Criando conta para:", cleanEmail);

 try {
 const { data, error } = await supabase.auth.signUp({ 
 email: cleanEmail, 
 password: cleanPassword,
 options: {
 data: {
 full_name: cleanName,
 phone: phone.trim(),
 birth_date: parseBRLDateToDB(birthDate),
 gender: gender,
 is_player: isPlayer,
 team_1: isPlayer ? team : "",
 number_1: isPlayer ? number : "",
 is_vaqueiro: isVaqueiro,
 horse_name: isVaqueiro ? horseName : "",
 is_motocross: isMotocross,
 is_noiva: isNoiva
 }
 }
 });
 
 if (error) {
 console.error("[SignUp] Erro do Supabase Auth:", error.message, error);
 throw error;
 }
 
 if (data.user) {
 console.log("[SignUp] Sucesso! Perfil será criado via Trigger.");
 }
 
 if (data.session) {
    if (data.user && avatarFile) {
      try {
        await new Promise(resolve => setTimeout(resolve, 1500));
        const fileExt = avatarFile.name.split('.').pop() || 'jpg';
        const filePath = `${data.user.id}/avatar-${Date.now()}.${fileExt}`;
        const uploadRes = await supabase.storage.from('avatars').upload(filePath, avatarFile, { upsert: true });
        
        if (!uploadRes.error && uploadRes.data) {
          const { data: publicData } = supabase.storage.from('avatars').getPublicUrl(uploadRes.data.path);
          if (publicData.publicUrl) {
            const { error: profileError } = await supabase.from('profiles').update({ avatar_url: publicData.publicUrl }).eq('user_id', data.user.id);
            if (profileError) console.error("Erro ao atualizar avatar no profile:", profileError);
          }
        }
      } catch (e) {
        console.error("Erro ao fazer upload da foto no signup:", e);
      }
    }

    toast.success("Bem-vindo! Sua conta foi criada com sucesso.");
    navigate("/home", { replace: true });
  } else {
    toast.info("Tudo certo! Verifique seu e-mail para ativar sua conta e liberar o acesso. Se não encontrar, verifique a caixa de SPAM.", { duration: 15000 });
    setIsSignUp(false);
  }
 } catch (error: any) {
 console.error("[SignUp] Erro detalhado:", error.message || error);
 if (error.details) console.error("[SignUp] Detalhes:", error.details);
 if (error.hint) console.error("[SignUp] Dica:", error.hint);
 
 const msg = error.message || "";
 if (msg.toLowerCase().includes("already registered") || msg.toLowerCase().includes("already exists")) {
 toast.error("Este e-mail já possui cadastro. Faça login ou recupere sua senha.");
 } else if (msg.toLowerCase().includes("weak_password") || msg.toLowerCase().includes("6 characters")) {
 toast.error("Senha muito fraca. Tente uma com pelo menos 6 caracteres.");
 } else {
 toast.error(msg || "Erro ao criar conta. Verifique sua conexão ou tente novamente.");
 }
 }
 finally { setLoading(false); }
 };

 const activeBg = bgSlides[currentBg] || heroBg;

  return (
    <div className="min-h-screen w-full relative bg-background select-none overflow-y-auto overflow-x-hidden">
      
      {/* ─── SECTION 1: LOGIN HERO ─── */}
      <div className="relative w-full min-h-[100vh] flex flex-col pb-20 md:pb-0">
        
        {/* ─── BACKGROUND HERO ─── */}
        <div className="absolute inset-0 z-0">
 <AnimatePresence mode="wait">
 <motion.img
 key={activeBg}
 initial={{ scale: 1.08, opacity: 0 }}
 animate={{ scale: 1, opacity: 1 }}
 exit={{ opacity: 0 }}
 transition={{ duration: 2.5, ease: "easeInOut" }}
 src={activeBg}
 alt="Background"
 className="w-full h-full object-cover"
 />
 </AnimatePresence>
 {/* Mobile: dark fade from middle down */}
 <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black md:hidden" />
 {/* Desktop: subtle dark overlay */}
 <div className="hidden md:block absolute inset-0 bg-foreground/10 " />
 </div>

 {/* ─── LOGO (Desktop & Mobile top) ─── */}
 <motion.div
 initial={{ opacity: 0, y: -16 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.8, delay: 0.2 }}
 className="relative z-20 flex justify-center pt-10 md:pt-12 pointer-events-none"
 >
 {appLogoUrl ? (
 <img
 src={appLogoUrl}
 style={{ width: `${appLogoSize}px` }}
 className="h-auto object-contain max-w-[55vw] drop-shadow-[0_4px_24px_rgba(0,0,0,0.9)]"
 alt="Logo"
 draggable={false}
 />
 ) : (
 /* Placeholder enquanto carrega */
 <div className="w-28 h-20 opacity-0" />
 )}
 </motion.div>

 {/* ─── LOGIN CARD WRAPPER ─── */}
 {/* Login: fixo no rodapé | Signup: fluxo normal scrollável */}
 <div className={isSignUp
 ? "relative z-30 flex justify-center pt-[30vh] pb-12 px-4"
 : "fixed bottom-0 left-0 right-0 z-30 flex justify-center"
 }>
 <motion.div
 layout
 initial={{ y: 80, opacity: 0 }}
 animate={{ y: 0, opacity: 1 }}
 transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
 className={[
 isSignUp ? "w-full max-w-[440px]" : "w-full md:max-w-[440px]",
 "bg-foreground/10 backdrop-blur-[40px]",
 isSignUp
 ? "border border-foreground/10 rounded-[32px]"
 : "border-t border-foreground/10 md:border md:border-foreground/10 rounded-t-[36px] md:rounded-[32px] md:mb-8",
 "shadow-[0_-12px_60px_rgba(0,0,0,0.7)] md:shadow-[0_20px_80px_rgba(0,0,0,0.8)]",
 "px-6 pt-6 pb-10 md:px-8 md:pt-8 md:pb-8",
 ].join(" ")}
 >
 {/* Avatar flutuando acima do card (login mode) */}
 {(!isSignUp && !isInstagramLogin) && (
 <div className="absolute -top-11 left-7 md:left-8">
 <div className="relative w-[72px] h-[72px]">
 <div className="w-full h-full rounded-full bg-foreground/10 backdrop-blur-xl border border-foreground/10 shadow-2xl overflow-hidden ring-4 ring-[#FF6A00]/20">
 {lastUser?.avatar ? (
 <img
 src={lastUser.avatar.startsWith("http") ? lastUser.avatar : supabase.storage.from("avatars").getPublicUrl(lastUser.avatar).data.publicUrl}
 className="w-full h-full object-cover"
 alt="Usuário"
 />
 ) : (
 <User className="w-full h-full p-4 text-foreground/40 " />
 )}
 </div>
 <div className="absolute bottom-0 right-0 w-5 h-5 bg-[#FF6A00] rounded-full flex items-center justify-center border border-background shadow-lg z-10">
 <CheckCircle2 className="w-3 h-3 text-white" />
 </div>
 </div>
 </div>
 )}

 {/* Suporte (canto superior direito) */}
 {!isSignUp && (
 <div className="absolute top-5 right-5 md:top-6 md:right-6 flex items-start gap-4">
 {/* Botão Instalar (Apenas se não instalado) */}
 {!isInstalled && (
 <button
 onClick={() => {
 localStorage.removeItem("pwa-install-dismissed");
window.dispatchEvent(new Event("force-pwa-prompt"));
 }}
 className="flex flex-col items-center gap-1 text-primary hover:text-primary/80 transition-colors"
 >
 <div className="w-9 h-9 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center hover:bg-primary/20 transition-colors">
 <Download className="w-4 h-4" />
 </div>
 <span className="text-[9px] font-bold uppercase tracking-widest">
   <TypeAnimation sequence={["INSTALAR", 2000, "BAIXAR", 2000]} wrapper="span" cursor={true} repeat={Infinity} />
 </span>
 </button>
 )}

 {/* Botão Suporte */}
 <button
 onClick={handleSupport}
 className="flex flex-col items-center gap-1 text-foreground/40 hover:text-[#25D366] transition-colors group"
 >
 <div className="w-9 h-9 rounded-2xl bg-foreground/10 border border-foreground/10 flex items-center justify-center group-hover:bg-[#25D366]/20 group-hover:border-[#25D366]/30 transition-colors">
  <svg className="w-4 h-4 group-hover:text-[#25D366] transition-colors" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
  </svg>
 </div>
 <span className="text-[9px] font-bold uppercase tracking-widest group-hover:text-[#25D366] transition-colors">WhatsApp</span>
 </button>
 </div>
 )}

 <div className={(!isSignUp && !isInstagramLogin) ? "mt-10 space-y-5" : "mt-1 space-y-5"}>

 {/* Greeting */}
 <div className="space-y-0.5">
 <h2 className="text-xl md:text-2xl font-bold text-foreground leading-tight">
 {isInstagramLogin
 ? "Entrada Rápida ⚡"
 : isForgotPassword 
 ? "Recuperar acesso 🔑" 
 : isSignUp 
 ? "Criar nova conta" 
 : lastUser 
 ? `Olá, ${lastUser.name.split(" ")[0]}` 
 : "Bem-vindo"} 👋
 </h2>
 {(lastUser && !isSignUp && !isForgotPassword) && (
 <button
 onClick={() => { setLastUser(null); setEmail(""); }}
 className="flex items-center gap-1 text-[#FF6A00] text-[11px] font-black tracking-wide"
 >
 Não é você? <ArrowRight className="w-3 h-3" />
 </button>
 )}
 {isForgotPassword && (
 <p className="text-foreground/40 text-[11px] font-medium transition-all">
 {resetEmailSent 
 ? "Verifique seu e-mail para o link de alteração." 
 : "Insira seu e-mail para receber o link de recuperação."}
 </p>
 )}
 </div>

 {/* Form */}
 <form 
 onSubmit={isInstagramLogin ? handleInstagramQuickLogin : (isForgotPassword ? handleResetPassword : (isSignUp ? handleSignUp : handleLogin))} 
 className="space-y-4"
 >
 
 <AnimatePresence mode="wait">
 {isInstagramLogin ? (
 <motion.div
 key="instagram"
 initial={{ opacity: 0, x: 20 }}
 animate={{ opacity: 1, x: 0 }}
 exit={{ opacity: 0, x: -20 }}
 className="space-y-4"
 >
 <div className="p-3 bg-gradient-to-r from-purple-600/20 to-pink-500/20 border border-purple-500/20 rounded-2xl">
 <p className="text-[11px] text-foreground/40 leading-relaxed font-medium">
 📸 <strong>Sem senhas ou e-mails!</strong> Entre instantaneamente informando seu usuário do Instagram. Suas compras e fotos ficam vinculadas a ele.
 </p>
 </div>

 <div className="space-y-3">
 <div className="relative group">
 <div className="absolute left-4 top-1/2 -translate-y-1/2 text-pink-500 font-bold text-sm">@</div>
 <Input
 type="text"
 value={instagramUser}
 onChange={(e) => setInstagramUser(e.target.value)}
 className="h-12 pl-10 bg-foreground/5 border-foreground/10 text-foreground placeholder:text-foreground/40 focus:ring-1 focus:ring-pink-500/30 rounded-2xl focus:border-pink-500/40"
 placeholder="seu_instagram"
 required
 disabled={loading}
 />
 </div>

 <div className="relative group">
 <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40 group-focus-within:text-pink-500 transition-colors" />
 <Input
 type="tel"
 value={instagramPhone}
 onChange={(e) => setInstagramPhone(e.target.value)}
 className="h-12 pl-11 bg-foreground/5 border-foreground/10 text-foreground placeholder:text-foreground/40 focus:ring-1 focus:ring-pink-500/30 rounded-2xl focus:border-pink-500/40"
 placeholder="WhatsApp (ex: 11999999999)"
 required
 disabled={loading}
 />
 </div>

 <div className="flex items-start gap-2 pt-2 px-1">
 <Checkbox 
 id="terms" 
 checked={acceptTerms} 
 onCheckedChange={(v) => setAcceptTerms(v as boolean)} 
 className="border-foreground/10 mt-0.5" 
 />
 <label htmlFor="terms" className="text-[11px] text-foreground/40 select-none leading-relaxed cursor-pointer">
 Eu declaro ser o titular deste Instagram e **autorizo a vinculação de minhas fotos** e o acesso rápido a esta plataforma.
 </label>
 </div>
 </div>

 <Button
 type="submit"
 disabled={loading}
 className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-foreground h-13 rounded-2xl text-base font-bold shadow-lg shadow-pink-500/10 hover:scale-[1.01] active:scale-[0.99] transition-all"
 style={{ height: "52px" }}
 >
 {loading ? "Autorizando entrada..." : "Acessar Rapidamente ⚡"}
 </Button>

 <button
 type="button"
 onClick={() => { setIsInstagramLogin(false); }}
 className="w-full text-center text-xs text-foreground/40 hover:text-foreground/40 font-medium transition-all"
 >
 Voltar ao login padrão
 </button>
 </motion.div>
 ) : isForgotPassword ? (
 <motion.div
 key="forgot"
 initial={{ opacity: 0, x: 20 }}
 animate={{ opacity: 1, x: 0 }}
 exit={{ opacity: 0, x: -20 }}
 className="space-y-4"
 >
 <p className="text-foreground/40 text-[11px] leading-relaxed">
 Insira seu e-mail cadastrado abaixo. Enviaremos um link seguro para você criar uma nova senha instantaneamente.
 </p>
 
 <div className="relative group">
 <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40 group-focus-within:text-primary transition-colors" />
 <Input
 type="email"
 value={email}
 onChange={(e) => setEmail(e.target.value)}
 className="h-12 pl-11 bg-foreground/5 border-foreground/10 text-foreground placeholder:text-foreground/40 focus:ring-1 focus:ring-primary/30 rounded-2xl"
 placeholder="seu@email.com"
 required
 />
 </div>

 <Button
 type="submit"
 disabled={loading || resetEmailSent}
 className="w-full bg-primary text-primary-foreground h-13 rounded-2xl text-sm font-bold shadow-lg shadow-primary/20"
 >
 {loading ? "Enviando..." : resetEmailSent ? "E-mail enviado! ✔️" : "Enviar link de recuperação"}
 </Button>

 <button
 type="button"
 onClick={() => { setIsForgotPassword(false); setResetEmailSent(false); }}
 className="w-full text-center text-xs text-foreground/40 hover:text-foreground/40 font-medium transition-all"
 >
 Cancelar e voltar ao login
 </button>
 </motion.div>
 ) : (
 <motion.div
 key="auth"
 initial={{ opacity: 0, x: -20 }}
 animate={{ opacity: 1, x: 0 }}
 exit={{ opacity: 0, x: 20 }}
 className="space-y-3"
 >
 {/* SignUp extra fields */}
 <AnimatePresence>
 {isSignUp && (
 <motion.div 
 initial={{ opacity: 0, height: 0, scale: 0.95 }}
 animate={{ opacity: 1, height: "auto", scale: 1 }}
 exit={{ opacity: 0, height: 0, scale: 0.95 }}
 transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
 className="space-y-3 mb-3 overflow-hidden"
 >
 {/* Avatar */}
 <label className="flex items-center gap-4 cursor-pointer group mb-4">
 <div className="w-16 h-16 rounded-2xl bg-foreground/10 border border-foreground/10 overflow-hidden flex items-center justify-center shrink-0">
 {avatarPreview ? <img src={avatarPreview} className="w-full h-full object-cover" alt="" /> : <Camera className="w-7 h-7 text-foreground/40 group-hover:text-primary transition-colors" />}
 </div>
 <div>
 <p className="text-sm font-bold text-foreground">Foto de perfil <span className="text-foreground/40 font-normal text-xs">(opcional)</span></p>
 <p className="text-xs text-foreground/40 ">Toque para selecionar</p>
 </div>
 <input type="file" onChange={(e) => { const f = e.target.files?.[0]; if (f) { setAvatarFile(f); setAvatarPreview(URL.createObjectURL(f)); }}} className="hidden" accept="image/*" />
 </label>

 <div className="relative group">
 <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40 group-focus-within:text-primary transition-colors" />
 <Input value={name} onChange={(e) => setName(e.target.value)} className="h-12 pl-11 bg-foreground/5 border-foreground/10 text-foreground placeholder:text-foreground/40 focus:ring-1 focus:ring-primary/30 rounded-2xl" placeholder="Nome completo" />
 </div>

 <div className="grid grid-cols-2 gap-3">
 <div className="relative group">
 <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40 group-focus-within:text-primary transition-colors" />
 <Input type="text" placeholder="DD/MM/AAAA" maxLength={10} value={birthDate} onChange={(e) => setBirthDate(formatToBRLDate(e.target.value))} className="h-12 pl-10 bg-foreground/5 border-foreground/10 text-foreground focus:ring-1 focus:ring-primary/30 rounded-2xl" />
 </div>
 <div className="relative group">
 <Gamepad2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40 z-10 pointer-events-none" />
 <select
 value={gender}
 onChange={(e) => setGender(e.target.value)}
 className="w-full h-12 pl-10 pr-4 bg-card border border-foreground/10 text-foreground rounded-2xl focus:ring-1 focus:ring-primary/30 outline-none appearance-none cursor-pointer"
 style={{ colorScheme: "dark" }}
 >
 <option value="" className="bg-card text-foreground/40 ">Gênero</option>
 <option value="Masculino" className="bg-card text-foreground">Masculino</option>
 <option value="Feminino" className="bg-card text-foreground">Feminino</option>
 <option value="Outro" className="bg-card text-foreground">Outro</option>
 </select>
 <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
 <svg className="w-4 h-4 text-foreground/40 " fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
 </svg>
 </div>
 </div>
 </div>

 <div className="relative group">
 <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40 group-focus-within:text-primary transition-colors" />
 <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="h-12 pl-11 bg-foreground/5 border-foreground/10 text-foreground placeholder:text-foreground/40 focus:ring-1 focus:ring-primary/30 rounded-2xl" placeholder="WhatsApp" />
 </div>

 <button
 type="button"
 onClick={() => setIsPlayer(p => !p)}
 className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-all ${
 isPlayer ? "bg-primary/10 border-primary/40" : "bg-foreground/5 border-foreground/10 "
 }`}
 >
 <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all ${
 isPlayer ? "bg-primary shadow-lg shadow-primary/30" : "bg-foreground/10 "
 }`}>
 <Gamepad2 className={`w-5 h-5 ${isPlayer ? "text-foreground" : "text-foreground/40 "}`} />
 </div>
 <div className="flex-1 text-left">
 <p className={`text-sm font-bold ${isPlayer ? "text-primary" : "text-foreground/40 "}`}>Sou jogador</p>
 <p className="text-[10px] text-foreground/40 ">Ativar modo jogador para aparecer no ranking</p>
 </div>
 <div className={`w-10 h-6 rounded-full transition-all relative ${isPlayer ? "bg-primary" : "bg-foreground/10 "}`}>
 <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${isPlayer ? "left-[18px]" : "left-0.5"}`} />
 </div>
 </button>

 {isPlayer && (
 <motion.div 
 initial={{ opacity: 0, scale: 0.95 }}
 animate={{ opacity: 1, scale: 1 }}
 className="grid grid-cols-2 gap-3"
 >
 <div className="relative group">
 <Input 
 value={team} 
 onChange={(e) => setTeam(e.target.value)} 
 className="h-12 bg-foreground/5 border-foreground/10 text-foreground placeholder:text-foreground/40 focus:ring-1 focus:ring-primary/30 rounded-2xl" 
 placeholder="Nome do Time" 
 />
 </div>
 <div className="relative group">
 <Input 
 value={number} 
 onChange={(e) => setNumber(e.target.value)} 
 className="h-12 bg-foreground/5 border-foreground/10 text-foreground placeholder:text-foreground/40 focus:ring-1 focus:ring-primary/30 rounded-2xl text-center" 
 placeholder="Nº Camisa" 
 />
 </div>
 </motion.div>
 )}

 <button
 type="button"
 onClick={() => setIsVaqueiro(v => !v)}
 className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-all ${
 isVaqueiro ? "bg-primary/10 border-primary/40" : "bg-foreground/5 border-foreground/10 "
 }`}
 >
 <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all ${
 isVaqueiro ? "bg-primary shadow-lg shadow-primary/30" : "bg-foreground/10 "
 }`}>
 <Trophy className={`w-5 h-5 ${isVaqueiro ? "text-foreground" : "text-foreground/40 "}`} />
 </div>
 <div className="flex-1 text-left">
 <p className={`text-sm font-bold ${isVaqueiro ? "text-primary" : "text-foreground/40 "}`}>Sou vaqueiro</p>
 <p className="text-[10px] text-foreground/40 ">Ativar modo vaqueiro para aparecer no ranking</p>
 </div>
 <div className={`w-10 h-6 rounded-full transition-all relative ${isVaqueiro ? "bg-primary" : "bg-foreground/10 "}`}>
 <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${isVaqueiro ? "left-[18px]" : "left-0.5"}`} />
 </div>
 </button>

 {isVaqueiro && (
 <motion.div 
 initial={{ opacity: 0, scale: 0.95 }}
 animate={{ opacity: 1, scale: 1 }}
 className="grid grid-cols-1 gap-3"
 >
 <div className="relative group">
 <Input 
 value={horseName} 
 onChange={(e) => setHorseName(e.target.value)} 
 className="h-12 bg-foreground/5 border-foreground/10 text-foreground placeholder:text-foreground/40 focus:ring-1 focus:ring-primary/30 rounded-2xl" 
 placeholder="Nome do seu cavalo" 
 />
 </div>
 </motion.div>
 )}

 <button
 type="button"
 onClick={() => setIsMotocross(m => !m)}
 className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-all ${
 isMotocross ? "bg-primary/10 border-primary/40" : "bg-foreground/5 border-foreground/10 "
 }`}
 >
 <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all ${
 isMotocross ? "bg-primary shadow-lg shadow-primary/30" : "bg-foreground/10 "
 }`}>
 <Bike className={`w-5 h-5 ${isMotocross ? "text-foreground" : "text-foreground/40 "}`} />
 </div>
 <div className="flex-1 text-left">
 <p className={`text-sm font-bold ${isMotocross ? "text-primary" : "text-foreground/40 "}`}>Sou Motocross</p>
 <p className="text-[10px] text-foreground/40 ">Ativar modo motocross para aparecer no ranking</p>
 </div>
 <div className={`w-10 h-6 rounded-full transition-all relative ${isMotocross ? "bg-primary" : "bg-foreground/10 "}`}>
 <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${isMotocross ? "left-[18px]" : "left-0.5"}`} />
 </div>
 </button>

 <button
 type="button"
 onClick={() => setIsNoiva(n => !n)}
 className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-all ${
 isNoiva ? "bg-primary/10 border-primary/40" : "bg-foreground/5 border-foreground/10 "
 }`}
 >
 <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all ${
 isNoiva ? "bg-primary shadow-lg shadow-primary/30" : "bg-foreground/10 "
 }`}>
 <Heart className={`w-5 h-5 ${isNoiva ? "text-foreground" : "text-foreground/40 "}`} />
 </div>
 <div className="flex-1 text-left">
 <p className={`text-sm font-bold ${isNoiva ? "text-primary" : "text-foreground/40 "}`}>Sou Noiva</p>
 <p className="text-[10px] text-foreground/40 ">Ativar modo noiva para aparecer no ranking</p>
 </div>
 <div className={`w-10 h-6 rounded-full transition-all relative ${isNoiva ? "bg-primary" : "bg-foreground/10 "}`}>
 <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${isNoiva ? "left-[18px]" : "left-0.5"}`} />
 </div>
 </button>
 </motion.div>
 )}
 </AnimatePresence>

 {/* Email & Password */}
 <div className="space-y-3">
 <div className="relative group">
 <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40 group-focus-within:text-primary transition-colors" />
 <Input
 type="email"
 value={email}
 onChange={(e) => setEmail(e.target.value)}
 className="h-12 pl-11 bg-foreground/5 border-foreground/10 text-foreground placeholder:text-foreground/40 focus:ring-1 focus:ring-primary/30 rounded-2xl"
 placeholder="E-mail"
 required
 />
 </div>

 <div className="relative group">
 <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40 group-focus-within:text-primary transition-colors" />
 <Input
 type={showPassword ? "text" : "password"}
 value={password}
 onChange={(e) => setPassword(e.target.value)}
 className="h-12 pl-11 pr-12 bg-foreground/5 border-foreground/10 text-foreground placeholder:text-foreground/40 focus:ring-1 focus:ring-primary/30 rounded-2xl"
 placeholder="Senha"
 required
 />
 <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-foreground/40 transition-colors">
 {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
 </button>
 </div>

 {!isSignUp && (
 <div className="flex items-center justify-between px-1">
 <label className="flex items-center gap-2 cursor-pointer">
 <Checkbox id="remember" checked={rememberMe} onCheckedChange={(v) => setRememberMe(v as boolean)} className="border-foreground/10 " />
 <span className="text-xs text-foreground/40 select-none">Lembrar de mim</span>
 </label>
 <button type="button" onClick={() => setIsForgotPassword(true)} className="text-primary text-xs font-semibold hover:underline">
 Esqueci a senha
 </button>
 </div>
 )}

 <Button
 type="submit"
 disabled={loading}
 className="w-full bg-gradient-primary text-foreground h-13 rounded-2xl text-base font-bold shadow-lg shadow-primary/20 hover:scale-[1.01] active:scale-[0.99] transition-all"
 style={{ height: "52px" }}
 >
 {loading ? "Processando..." : isSignUp ? "Criar conta" : "Acessar plataforma"}
 </Button>

 <div className="text-center pt-4">
 {isSignUp ? (
 <p className="text-sm text-foreground/40 ">
 Já tem uma conta?{" "}
 <button
 type="button"
 onClick={() => setIsSignUp(false)}
 className="text-primary font-bold hover:underline"
 >
 Fazer Login
 </button>
 </p>
 ) : (
 <div className="flex flex-col items-center gap-3 w-full">
 <div className="flex items-center justify-center my-1 w-full gap-3">
 <div className="flex-1 border-t border-foreground/10" />
 <span className="text-[9px] uppercase font-black tracking-widest text-foreground/40">OU</span>
 <div className="flex-1 border-t border-foreground/10" />
 </div>

 <div className="grid grid-cols-2 gap-2 w-full">
    <Button
    type="button"
    variant="outline"
    onClick={() => { setIsInstagramLogin(true); setIsSignUp(false); setIsForgotPassword(false); }}
    className="w-full border-primary/30 text-primary hover:bg-primary/10 h-12 rounded-2xl font-black tracking-widest text-xs transition-all hover:border-primary/60 flex items-center justify-center gap-2 px-1"
    >
    <Instagram className="w-4 h-4 shrink-0" />
    <span className="truncate">Instagram</span>
    </Button>

    <Button
    type="button"
    variant="outline"
    onClick={async () => {
      try {
        setLoading(true);
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: { redirectTo: window.location.origin }
        });
        if (error) throw error;
      } catch (err: any) {
        toast.error(err.message || "Erro ao entrar com Google.");
        setLoading(false);
      }
    }}
    className="w-full border-primary/30 text-primary hover:bg-primary/10 h-12 rounded-2xl font-black tracking-widest text-xs transition-all hover:border-primary/60 flex items-center justify-center gap-2 px-1"
    >
    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
    <span className="truncate">Google</span>
    </Button>
  </div>

 <p className="text-xs font-medium text-foreground/40 tracking-widest mt-2">ainda não tem cadastro?</p>
 <Button
 type="button"
 variant="outline"
 onClick={() => setIsSignUp(true)}
 className="w-full border-primary/30 text-primary hover:bg-primary/10 h-12 rounded-2xl font-black tracking-widest text-xs transition-all hover:border-primary/60"
 >
 criar nova conta
 </Button>
 </div>
 )}
 </div>
 </div>
 </motion.div>
 )}
 </AnimatePresence>

 {/* Admin link */}
 {!isForgotPassword && (
 <button
 onClick={() => navigate("/admin")}
 className="w-full text-center text-foreground/40 hover:text-foreground/40 text-[10px] font-medium transition-all pt-2"
 >
 Acesso administrativo
 </button>
 )}
 </form>
 
 <div className="mt-8 flex flex-col items-center gap-2">
 <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest font-black text-foreground/40 ">
 <button onClick={() => navigate("/privacy")} className="hover:text-primary transition-colors">Políticas</button>
 <div className="w-1 h-1 rounded-full bg-foreground/10 " />
 <button onClick={() => navigate("/subscription")} className="hover:text-primary transition-colors">Termos</button>
 </div>
 <p className="text-[10px] text-foreground/40 font-bold uppercase tracking-tight">
 Seus dados de pagamento são protegidos e salvos via Mercado Pago 🛡️
 </p>
          </div>
        </div>
      </motion.div>
    </div>
  </div>

      {/* ─── FAIXA AUDIOVISUAL SEPARADORA ─── */}
      <div className="w-full relative z-40 -mt-10 py-3 bg-gradient-to-r from-orange-500 via-yellow-500 to-orange-500 skew-y-[-2deg] flex items-center justify-center overflow-hidden shadow-[0_0_40px_rgba(249,115,22,0.4)]">
        <div className="flex whitespace-nowrap opacity-90">
          <span className="text-black font-black uppercase text-xl md:text-3xl tracking-[0.3em] mx-4 animate-pulse">
            AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL •
          </span>
          <span className="text-black font-black uppercase text-xl md:text-3xl tracking-[0.3em] mx-4 animate-pulse">
            AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL • AUDIOVISUAL •
          </span>
        </div>
      </div>

      {/* ─── SECTION 2: PORTFÓLIO IMERSIVO ─── */}
      <div className="relative z-30 pt-16 bg-background">
         <PortfolioImersivo />
      </div>

    </div>
  );
};

export default Login;
